import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker, Tag } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { PointType, PointTypePaginationFilter } from '@/models/point-type.model';
import { getPagedPointTypes } from '@/services/point-type.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const activeFilterList = [
  { value: 1, label: 'Active' },
  { value: 0, label: 'Inactive' },
];

const PointTypeList: React.FC = () => {
  const [filterReq, setFilterReq] = useState<PointTypePaginationFilter>(new PointTypePaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<PointType[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: PointTypePaginationFilter) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<PointType> = await getPagedPointTypes(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching point types: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({ ...pre, orderBy: sortColumn, isAscending: sortType == 'asc' }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav('/point-types/new')}>
          Add Point Type
        </Button>
        <Stack spacing={6}>
          <SelectPicker
            label="Status"
            data={activeFilterList}
            searchable={false}
            value={filterReq.isActive == undefined ? null : (filterReq.isActive ? 1 : 0)}
            onChange={val => setFilterReq(pre => ({ ...pre, isActive: val == null ? undefined : val == 1 }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={val => setFilterReq(pre => ({ ...pre, keyword: val }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center">
          <HeaderCell>Icon</HeaderCell>
          <ImageCell dataKey="iconUrl" />
        </Column>
        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>Key</HeaderCell>
          <Cell dataKey="key" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={3} align="left" fullText>
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>
        <Column flexGrow={0.7} align="center" sortable>
          <HeaderCell>Active</HeaderCell>
          <Cell dataKey="isActive">
            {(rowData: PointType) => <Tag color={rowData.isActive ? 'green' : 'red'}>{rowData.isActive ? 'Active' : 'Inactive'}</Tag>}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Created At</HeaderCell>
          <Cell dataKey="createdAt" fullText>
            {(rowData: PointType) => jsDateToZone(rowData.createdAt)}
          </Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: PointType) => (
              <IconButton onClick={() => nav(`/point-types/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default PointTypeList;
